import type { FC } from "react";
import { useState } from "react";
import styled from "@emotion/styled";
import type { ChatManagerResult } from "@/hooks/use-chat-manager";
import {
  CustomModalOverlay,
  Modal,
  ModalContent,
  ModalPortal,
} from "../primitives/modal";
import { ChatContent } from "./chat-content";
import { ParagraphRegular } from "../primitives/typographies";
import { hideScrollbar, spacing } from "../design-tokens";
import { ModalHeader } from "../primitives/modal-header";
import { SourcesList } from "./sources-list";

type ModalMode = "chat" | "sources";

// Custom modal content for chat - wider and taller
const ChatModalContent = styled(ModalContent)`
  width: 90vw;
  max-width: 800px;
  max-height: 80vh;
  min-height: 400px;
  height: fit-content;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: ${spacing["3xl"]}px;
  box-sizing: border-box;
  text-align: left;
  z-index: 9999;
  position: fixed;
  ${hideScrollbar}

  @media (max-width: 768px) {
    width: 95vw;
    height: 85vh;
    max-height: 85vh;
    min-height: 300px;
  }
`;

const ModalContentBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${spacing.xl}px;
  min-height: 0;
  overflow-y: auto;
  box-sizing: border-box;
  text-align: left;
`;

type ChatModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialMode?: ModalMode;
  chatManager: ChatManagerResult;
  container?: Element | null;
};

const ChatContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  box-sizing: border-box;
`;

export const ChatModal: FC<ChatModalProps> = ({
  isOpen,
  onOpenChange,
  initialMode = "chat",
  chatManager,
  container,
}) => {
  const [mode, setMode] = useState<ModalMode>(initialMode);

  const handleModeChange = (newMode: ModalMode) => {
    if (mode === "chat" && newMode === "sources") {
      setMode(newMode);
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <ModalPortal container={container}>
        {isOpen && (
          <>
            <CustomModalOverlay
              isOpen={isOpen}
              onClick={() => onOpenChange(false)}
              data-vaul-overlay
            />
            <ChatModalContent data-state={isOpen ? "open" : "closed"}>
              {mode === "chat" && (
                <>
                  <ChatContainer>
                    <ChatContent
                      {...chatManager}
                      isModal
                      isSearchPage={false} // TODO: Remove hardcoded value when implementing search modal variant
                      onCloseClick={() => onOpenChange(false)}
                      onSourcesClick={() => handleModeChange("sources")}
                    />
                  </ChatContainer>
                </>
              )}

              {mode === "sources" && (
                <>
                  <ModalHeader
                    title={`Sources (${chatManager.response?.sources?.length || 0})`}
                    onBackClick={() => setMode("chat")}
                    isMultiModal
                  />
                  <ModalContentBody>
                    {chatManager.response?.sources &&
                    chatManager.response.sources.length > 0 ? (
                      <SourcesList sources={chatManager.response.sources} />
                    ) : (
                      <ParagraphRegular>
                        No sources available for this response.
                      </ParagraphRegular>
                    )}
                  </ModalContentBody>
                </>
              )}
            </ChatModalContent>
          </>
        )}
      </ModalPortal>
    </Modal>
  );
};
